import type { AvatarProps } from "./Avatar.tsx";
import { useT } from "../i18n/I18nProvider.tsx";
import { Avatar } from "./Avatar.tsx";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";
import { IconButton } from "./IconButton.tsx";
import { Text } from "./Text.tsx";
import { cx } from "./tokens.ts";
import "./call.css";

export type IncomingRingProps = {
  /** "Ana Souza" (or the conversation's title). */
  title: string;
  /** "Is calling you · Huddle in #design". */
  body: string;
  /** The caller's picture: name for initials and tone, photo. */
  caller: Pick<AvatarProps, "name" | "src" | "tone" | "emoji">;
  /** Join with the microphone / camera on (remembered: the huddle prefs `joinWithMicOff` / `joinWithCameraOff`). */
  mic: boolean;
  camera: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onJoin: () => void;
  onDecline: () => void;
  /** "Decline with a message…": declines and opens the conversation with the caller to write one. */
  onDeclineWithMessage?: () => void;
  /**
   * "window": the desktop call window (design "Main": dark, stacked, a × that only stops the ringing here, the
   * header doubles as the drag handle). "card" (default): the in-window card in the corner (design "InApp").
   */
  variant?: "card" | "window";
  /** Window: "Bundle · Bigsmall" above the caller. */
  label?: string;
  /** Window: × — stop ringing and hide; the caller keeps hearing it ring until it times out. */
  onClose?: () => void;
  /** Card: the ring is over; it fades out (the caller unmounts it after `--b-duration-normal`). */
  leaving?: boolean;
  testID: string;
};

/**
 * Someone is ringing me into a huddle (design "Huddle ring"): the caller's photo with a pulse, mic and camera to join
 * with, Decline / Join and "Decline with a message…". Slides in when it appears (`.b-ring`, reduced motion: none);
 * the caller decides how it leaves (the in-app card fades out through `Reveal`, the window hides).
 */
export function IncomingRing(p: IncomingRingProps) {
  const t = useT();
  const window = p.variant === "window";
  const toggles = (
    <div className="b-ring-toggles">
      <IconButton
        icon={p.mic ? "mic" : "micOff"}
        variant="filled"
        accessibilityLabel={t(p.mic ? "huddle.ring.micOn" : "huddle.ring.micOff")}
        onPress={p.onToggleMic}
        aria-pressed={p.mic}
        testID={`${p.testID}-mic`}
      />
      <IconButton
        icon={p.camera ? "video" : "videoOff"}
        variant="filled"
        accessibilityLabel={t(p.camera ? "huddle.ring.cameraOn" : "huddle.ring.cameraOff")}
        onPress={p.onToggleCamera}
        aria-pressed={p.camera}
        testID={`${p.testID}-camera`}
      />
    </div>
  );
  const message =
    p.onDeclineWithMessage === undefined ? null : (
      <button type="button" className="b-ring-message" onClick={p.onDeclineWithMessage} data-testid={`${p.testID}-message`}>
        {t("huddle.ring.declineWithMessage")}
      </button>
    );
  const photo = (
    <span className="b-ring-photo">
      <span className="b-ring-pulse" aria-hidden="true" />
      <Avatar {...p.caller} size={window ? "avatar.xl" : "avatar.lg"} />
    </span>
  );
  return (
    <section
      className={cx("b-ring", window ? "b-ring-window" : "b-ring-card", p.leaving === true && "b-ring-leaving")}
      role="alertdialog"
      aria-label={t("huddle.incoming.label")}
      data-testid={p.testID}
      {...(window ? { "data-theme": "dark" } : {})}
    >
      {window ? (
        <>
          <div className="b-ring-head" data-tauri-drag-region>
            <Text variant="caption" color="text.secondary" lines={1}>
              {p.label ?? ""}
            </Text>
            {p.onClose === undefined ? null : <IconButton icon="close" size="sm" variant="filled" accessibilityLabel={t("huddle.ring.close")} onPress={p.onClose} testID={`${p.testID}-close`} />}
          </div>
          <div className="b-ring-who" data-tauri-drag-region>
            {photo}
            <Text variant="headline" weight="semibold" lines={1} testID={`${p.testID}-title`}>
              {p.title}
            </Text>
            <Text variant="footnote" color="text.secondary" lines={2}>
              {p.body}
            </Text>
          </div>
          {toggles}
          <div className="b-ring-actions">
            <Button label={t("huddle.incoming.decline")} variant="destructive" onPress={p.onDecline} testID={`${p.testID}-decline`} />
            <Button label={t("huddle.incoming.join")} variant="success" icon="huddle" onPress={p.onJoin} testID={`${p.testID}-join`} />
          </div>
          {message}
        </>
      ) : (
        <>
          <div className="b-ring-who-row">
            {photo}
            <div className="b-ring-text">
              <Text weight="semibold" lines={1} testID={`${p.testID}-title`}>
                {p.title}
              </Text>
              <span className="b-ring-sub">
                <Icon name="huddle" size="icon.sm" color="status.success" />
                <Text variant="footnote" color="text.secondary" lines={1}>
                  {p.body}
                </Text>
              </span>
            </div>
          </div>
          <div className="b-ring-row">
            {toggles}
            <span className="b-ring-spacer" />
            <Button label={t("huddle.incoming.decline")} variant="secondary" size="sm" onPress={p.onDecline} testID={`${p.testID}-decline`} />
            <Button label={t("huddle.incoming.join")} variant="success" icon="huddle" size="sm" onPress={p.onJoin} testID={`${p.testID}-join`} />
          </div>
          {message}
        </>
      )}
    </section>
  );
}
